<template>
    <Teleport to="body" v-if="documentReady">
        <div id="compary_sticky" class="sticky-bar" @click="openCompare" v-show="canSee">
            <div class="summary-container">
                <div class="summary pdp-sticky-summary">
                    <div class="pdp-sticky-compare" v-if="compareCount">
                        <CompareLightIcon class="pdp-sticky-compare-icon" alt="compare-icon"/>
                        <div class="summary-text">
                            <p class="text-rothcoBlack" style="font-weight: 600">Compare</p>
                            <p class="text-rothcoBlack">{{ compareCount }} of 4 Added</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>
<script lang="ts" setup>
    import { useCompareDeckStore } from "@/store/compareDeck.ts";
    import { computed, inject, ref, onMounted, watch } from "vue";
    import { usePage, router }  from "@inertiajs/vue3";
    import { useGenericStore } from "@/store/genericStore";
    import { storeToRefs } from "pinia";
    import CompareDeck from "@/components/CompareDeck.vue"
    import CompareTable from "@/components/CompareTable.vue"
    import { useDialog } from "primevue/usedialog";
    import { useLists } from "@/helpers/composables/useList";
    import { useSettingsStore } from "@/store/settings";
    import { useSearchSpringCookiesStore } from "@/store/searchSpringCookies";
    import { useFetch } from "@/helpers/composables/useFetch";
    import CartPreview from "@/components/cart/CartPreview.vue";
    import ReceiveEmailModal from "@/components/ReceiveEmailModal.vue";
    import CompareLightIcon from "@icons/iconamoon_compare-light.svg";
    import { useAttentive } from "@/helpers/composables/attentive";
    import { useIntelliSuggest } from "@/helpers/composables/intellisuggest";
    let store = useCompareDeckStore();
    let route = inject('route');
    let { user, user_id, isLoggedIn } = storeToRefs(useSettingsStore());
    let { setList, toggleFlyout, setNotificationMessage } = useSettingsStore();
    let { is_dealer } = storeToRefs(useSettingsStore());
    let { addCartProduct} = useSearchSpringCookiesStore();
    let { addToList } = useLists({ router, route});
    let compareCount = computed(() => store.products.length );
    let buyStackVisibilityStore = useGenericStore('buyStackVisible');
    let expressVisibilityStore = useGenericStore('expressVisible');
    let { state: buyStackVisible } = storeToRefs(buyStackVisibilityStore);
    let { state: expressVisible} = storeToRefs(expressVisibilityStore);
    let isPdp = computed(() => {
        return usePage().component === 'ProductDetailPage';
    });
    let quickOrder = computed(() => {
        return usePage().component === 'QuickOrderingPage';
    });

    let isCheckout = computed(() => {
        return usePage().component.includes('DealerCheckout');
    });
    let { attentiveAddToCart} = useAttentive();

    let compareStickyVisibleStore = useGenericStore('compareStickyVisible');
    let { state: compareStickyVisible } = storeToRefs(compareStickyVisibleStore);
    let canSee = computed(()=>{
        if(isPdp.value){
            return buyStackVisible.value && !expressVisible.value && compareCount.value > 0;
        }else{
            return compareCount.value > 0 && !quickOrder.value && !isCheckout.value;
        }
    })
    watch(()=>(canSee.value),(newVal)=>(compareStickyVisible.value = newVal),{immediate: true})


    let dialog = useDialog();

    const openCompare = () => {
        dialog.open(CompareDeck, {
            props: {
                modal: true,
                closable: true,
                dismissableMask: true,
                showHeader: false
            },
            onClose(event){
                if(event?.data?.showComparisons){
                    openCompareTable();
                }
            }
        });
    }
    const { inBasket } = useIntelliSuggest({
        siteId: 'xxkp6b',
        shopperId: user_id.value ?? undefined,
    });
    const openCompareTable = () => {
        dialog.open(CompareTable, {
            props: {
                modal: true,
                closable: true,
                dismissableMask: true,
                showHeader: false
            },
            onClose(event){
                if(event?.data?.action === 'add_item' && event?.data?.item){
                    let item = event?.data.item;
                    addToList(item, {
                        afterFetch: () => {
                            if(!is_dealer.value){
                                attentiveAddToCart({
                                    ...item,
                                    price: item.current_unit_price
                                });
                            }
                            
                            return new Promise((resolve) => {
                                inBasket({
                                    items: [
                                        {
                                            sku: item.product_line_code,
                                            qty: item.quantity,
                                            price: item.price_to_use_with_discount,
                                        }
                                    ]
                                });
                                addCartProduct(item.product_line_code);
                                router.reload({only: ["current_list", "last_added_item"],
                                onSuccess: (data) => {
                                        setList(usePage().props.current_list);
                                        resolve(data);
                                        openCartPreview();
                                        
                                    }
                                });
                            });
                        }
                    });
                }else if(event?.data?.action === 'instock_email'){
                    let variationId = event?.data.variationId
                    openEmail(variationId);
                }
            }
        });
    }

    function openCartPreview(){
        dialog.open(CartPreview, {
            props: {
                modal: true,
                closable: true,
                dismissableMask: true,
            }
        });

        setTimeout(() => {
            dialog.close();
        }, 9000);
    }

    function openEmail(variationId){
        if(!isLoggedIn.value){
            toggleFlyout({
                type: 'info',
                headline: 'Signing up for Instock Alerts? ',
                message: 'Please log in or create an account to subscribe.',
                afterLoginAction: ()=>{
                    useFetch(route("products.instock_email"), {
                        afterFetch(ctx){
                            setNotificationMessage({
                                message: `You're all set! We'll notify you via email as soon as this product is in stock.`,
                                type: 'success'
                            })
                        },
                        onFetchError(ctx){
                            setNotificationMessage({
                                message: `There was an issue with your request, please try again.`,
                                type: 'error'
                            })
                        }
                    }).post({
                        instock_email: {
                            email: user.value.email,
                            item_variation_id: variationId
                        }
                    });
                },
                createAccountParams: {
                    instock_email: variationId
                }
            });
        }else{
            dialog.open(ReceiveEmailModal, {
                props: {
                    showHeader: false,
                    modal: true
                },
                data: {
                    itemVariationId: variationId
                }
            });
        }
    }

    const documentReady = ref(false);
    onMounted(() => {
        documentReady.value = !!document.body;
    });
    
</script>
<style lang="scss" scoped>

.sticky-bar {
    width: fit-content;
    position: fixed;
    top: unset;
}
</style>